import React from 'react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import SeoHead from '../components/SeoHead';

const AboutPage = () => {
  const academicBackground = [
    {
      year: "2020-2021",
      location: "Paris",
      institution: "ESCP Business School",
      degree: "Masters of Science (MSc) - Specialized Masters in Media Management",
      distinction: "With Jury Distinction"
    },
    {
      year: "2014-2015",
      location: "Milan",
      institution: "Istituto Europeo di Design (IED)",
      degree: "Masters in Event Management",
      distinction: null
    },
    {
      year: "2008-2011",
      location: "Paris",
      institution: "Cours Florent",
      degree: "B.A. Dramatic Arts (French & English Programs)",
      distinction: null
    },
    {
      year: "2006-2008",
      location: "Beirut",
      institution: "Université Saint-Joseph",
      degree: "Scenic and Audiovisual Studies (Major - IESAV)\nFrench Literature (Minor FLSH)",
      distinction: null
    }
  ];

  return (
    <div className="min-h-screen bg-cream">
      <SeoHead
        title="About Carol Mezher – Writer, Producer & Creative Consultant"
        description="Paris-based writer, creative producer and development executive working across film, television, cultural programming and large-scale live experiences for over fifteen years."
        path="/about"
      />
      <Header />

      {/* Hero Section */}
      <section className="pt-32 pb-0 md:pb-12 bg-midnight-blue overflow-hidden">
        <div className="container mx-auto px-8 md:px-16">
          <div className="max-w-7xl mx-auto">
            <div className="text-center">
              <h1 className="font-playfair font-bold text-5xl md:text-6xl text-white mb-6">
                About
              </h1>
              <h2
                className="font-playfair font-bold text-3xl md:text-4xl text-whale-blue mb-4"
                style={{ textShadow: '0 1px 2px rgba(0,0,0,0.6)' }}
              >
                Writer, Producer & Creative Consultant
              </h2>
            </div>
          </div>
        </div>
      </section>

      {/* Bio Section */}
      <section className="pt-8 md:pt-16 pb-12 md:pb-16 bg-[#DDE3EC]">
        <div className="container mx-auto px-8 md:px-16">
          <div className="max-w-7xl mx-auto">
            <div className="grid lg:grid-cols-12 gap-12 items-start">
              {/* Profile Image — sticky so it stays balanced alongside the longer bio */}
              <div className="lg:col-span-4 lg:sticky lg:top-32 self-start">
                <div className="w-full aspect-[3/4] md:rounded-2xl overflow-hidden shadow-xl md:border-4 md:border-white">
                  <img 
                    src="/assets/portfolio/carol-mezher-profile-image.webp"
                    alt="Carol Mezher — Writer, Producer and Creative Consultant, Paris"
                    className="w-full h-full object-cover object-top"
                  />
                </div>
              </div>

              {/* Bio Text */}
              <div className="lg:col-span-8 space-y-6 text-lg text-charcoal/80 leading-relaxed">
                <p>
                  A Paris-based writer, creative producer, and development executive working across film, television, cultural programming, and large-scale live experiences. For more than fifteen years, I have developed projects across Europe and the Arab world, moving between storytelling, creative strategy, production, and large-scale live events.
                </p>
                <p>
                  My early career took shape within Italy's creative powerhouse ecosystem, where I collaborated with major international production companies including Balich Wonder Studio and Filmmaster Events on high-profile live events, internationally broadcast ceremonies, and landmark productions. Working across ambitious cultural experiences shaped my approach to storytelling early on, grounding my work in the relationship between narrative, visual language, and audience engagement.
                </p>
                <p>
                  Over time, my focus led me back to my original passion for storytelling, expanding into film, television, and cultural programming. This evolution brought me to work closely with filmmakers, festivals, and creative institutions on project development, industry strategy, and international partnerships. Along the way, I collaborated with festivals and industry platforms including the Red Sea International Film Festival, Ajyal Film Festival, and the Doha Film Festival, experiences that deepened my engagement with regional storytelling and cross-cultural dialogue.
                </p>
                <p>
                  I have also worked in film and television development, contributing to the shaping and positioning of internationally oriented projects emerging from the Arab world. My work spans feature films, series, documentaries, festival programming, and creative consulting, combining artistic development with a strong understanding of international markets and audience strategy.
                </p>
                <p>
                  Having lived and worked between Milan, Paris, Beirut, Jeddah, and Doha, I continue to operate at the intersection of storytelling and cultural impact, developing narratives that are both locally rooted and globally resonant.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Academic Background + Languages + Programs & Seminars — horizontal ribbons */}
      <section className="py-0 md:py-16 bg-cream">
        <div className="container mx-auto px-0 md:px-16">
          <div className="space-y-0 md:space-y-6">

            {/* RIBBON 1 — Academic Background */}
            <div className="relative bg-midnight-blue md:rounded-2xl overflow-hidden shadow-xl">
              <div
                aria-hidden="true"
                className="pointer-events-none absolute inset-0 opacity-[0.06]"
                style={{
                  backgroundImage: 'url(/hero-pattern.png)',
                  backgroundRepeat: 'repeat',
                  backgroundSize: '500px auto',
                }}
              />
              <div className="relative z-10 grid grid-cols-1 md:grid-cols-[280px_1fr]">
                {/* Left rail */}
                <div className="p-8 md:p-10 md:border-r border-white/15 flex items-center justify-center">
                  <h2 className="font-playfair font-bold text-3xl md:text-4xl text-white leading-tight text-center [text-wrap:balance]">
                    Academic Background
                  </h2>
                </div>
                {/* Items */}
                <div className="p-8 md:p-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-x-8 gap-y-7">
                  {academicBackground.map((edu, index) => (
                    <div key={index} className="border-l-2 border-terracotta/72 pl-4">
                      <div className="flex flex-wrap items-baseline gap-x-3">
                        <span className="font-montserrat font-bold text-terracotta text-sm tracking-wider">
                          {edu.year}
                        </span>
                        <span className="font-montserrat text-white/70 text-xs uppercase tracking-widest">
                          {edu.location}
                        </span>
                      </div>
                      <h3 className="font-playfair font-bold text-lg text-white mt-1 leading-snug [text-wrap:balance]">
                        {edu.institution}
                      </h3>
                      <p className="text-white/90 text-sm font-montserrat font-medium leading-snug mt-1 [text-wrap:balance]" style={{ whiteSpace: 'pre-line' }}>
                        {edu.degree}
                      </p>
                      {edu.distinction && (
                        <p className="text-terracotta text-xs font-montserrat italic mt-1">
                          {edu.distinction}
                        </p>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* RIBBON 2 — Languages */}
            <div className="bg-terracotta md:rounded-2xl overflow-hidden shadow-xl">
              <div className="grid grid-cols-1 md:grid-cols-[280px_1fr]">
                {/* Left rail */}
                <div className="p-8 md:p-10 md:border-r border-midnight-blue/20 flex items-center justify-center">
                  <h2 className="font-playfair font-bold text-3xl md:text-4xl text-midnight-blue leading-tight text-center">
                    Languages
                  </h2>
                </div>
                {/* Items */}
                <div className="p-8 md:p-10 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-x-6 gap-y-5">
                  {[
                    { lang: 'French', level: 'Native' },
                    { lang: 'Arabic', level: 'Native' },
                    { lang: 'English', level: 'Professional' },
                    { lang: 'Italian', level: 'Professional' },
                    { lang: 'Spanish', level: 'Intermediate' },
                  ].map((item) => (
                    <div
                      key={item.lang}
                      className="border-l-2 border-midnight-blue/70 pl-4"
                    >
                      <p className="font-playfair font-bold text-xl text-whale-blue leading-tight">
                        {item.lang}
                      </p>
                      <p className="font-montserrat font-medium text-white text-sm md:text-base tracking-wide mt-1">
                        {item.level}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* RIBBON 3 — Programs & Seminars
                Same diamond-pattern backdrop as Ribbon 1 (Academic Background)
                so both midnight-blue ribbons read consistently. */}
            <div className="relative bg-midnight-blue md:rounded-2xl overflow-hidden shadow-xl">
              <div
                aria-hidden="true"
                className="pointer-events-none absolute inset-0 opacity-[0.06]"
                style={{
                  backgroundImage: 'url(/hero-pattern.png)',
                  backgroundRepeat: 'repeat',
                  backgroundSize: '500px auto',
                }}
              />
              <div className="relative z-10 grid grid-cols-1 md:grid-cols-[280px_1fr]">
                {/* Left rail */}
                <div className="p-8 md:p-10 md:border-r border-white/15 flex items-center justify-center">
                  <h2 className="font-playfair font-bold text-3xl md:text-4xl text-white leading-tight text-center">
                    Programs &amp;<br className="hidden md:inline" /> Seminars
                  </h2>
                </div>
                {/* Items */}
                <div className="p-8 md:p-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-7">
                  {[
                    {
                      year: '2017',
                      location: 'Paris',
                      organization: 'French Ministry of Culture & La Maison des Cultures du Monde',
                      program: 'Séjours Culture',
                    },
                    {
                      year: '2023',
                      location: 'Cannes',
                      organization: 'Cannes Series',
                      program: 'Writers Club',
                    },
                    {
                      year: '2024',
                      location: 'Rome',
                      organization: 'MIA: Mercato Internationale Audiovisivo',
                      program: 'Drama Co-production Market & Pitching Forum',
                    },
                    {
                      year: '2024 – 2025',
                      location: 'Doha',
                      organization: 'Doha Film Institute',
                      program: 'Development Grantee, Series Lab Alumni & Qumra Participant',
                    },
                    {
                      year: '2026',
                      location: 'Dubai',
                      organization: 'Netflix Grow Creative',
                      program: 'Story is Story',
                    },
                  ].map((item) => (
                    <div key={item.organization} className="border-l-2 border-terracotta/72 pl-4">
                      <div className="flex flex-wrap items-baseline gap-x-3">
                        <span className="font-montserrat font-bold text-terracotta text-sm tracking-wider">
                          {item.year}
                        </span>
                        <span className="font-montserrat text-white/70 text-xs uppercase tracking-widest">
                          {item.location}
                        </span>
                      </div>
                      <h3 className="font-playfair font-bold text-lg text-white mt-1 leading-snug [text-wrap:balance]">
                        {item.organization}
                      </h3>
                      <p className="text-white/90 text-sm font-montserrat font-medium leading-snug mt-1 [text-wrap:balance]">
                        {item.program}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            </div>

          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default AboutPage;
